import React from 'react';
import { Icon } from '@iconify/react';
import { LoyaltyActiveSystem } from '@/types/loyalty';
import { cn } from '@/lib/utils';

interface Props {
  value: LoyaltyActiveSystem;
  onChange: (system: LoyaltyActiveSystem) => void;
  isActive: boolean;
  onToggleActive: (active: boolean) => void;
  disabled?: boolean;
}

export const LoyaltySystemSelector: React.FC<Props> = ({
  value,
  onChange,
  isActive,
  onToggleActive,
  disabled = false,
}) => {
  const systems = [
    {
      id: 'points' as LoyaltyActiveSystem,
      title: 'Sistem poin',
      description:
        'Pelanggan mengumpulkan poin dari setiap transaksi, lalu menukarnya jadi potongan harga',
      icon: 'ph:coins',
    },
    {
      id: 'membership' as LoyaltyActiveSystem,
      title: 'Membership',
      description:
        'Pelanggan mendaftar jadi member dan mendapat diskon selama masa aktif',
      icon: 'ph:identification-card',
    },
    {
      id: 'stamp' as LoyaltyActiveSystem,
      title: 'Booking gratis',
      description:
        'Setelah booking sejumlah kali, pelanggan mendapat jam main gratis',
      icon: 'ph:ticket',
    },
  ];

  return (
    <div className="rounded-2xl border border-[#E0E6ED] bg-white p-5 sm:p-6 shadow-xs">
      {/* Top Row: Section Label & Program Active Toggle */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <span className="text-[11px] font-semibold uppercase tracking-[0.8px] text-[#8C8C8C]">
          SISTEM LOYALTI
        </span>

        <label
          className={cn(
            'inline-flex items-center gap-2.5 select-none',
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
          )}
        >
          <span
            className={cn(
              'text-xs font-medium transition-colors',
              isActive ? 'text-[#0E1726]' : 'text-[#8C8C8C]'
            )}
          >
            {isActive ? 'Program aktif' : 'Program nonaktif'}
          </span>
          <span className="relative inline-flex items-center">
            <input
              type="checkbox"
              disabled={disabled}
              checked={isActive}
              onChange={(e) => onToggleActive(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-10 h-5 bg-[#E0E6ED] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#FC480D]" />
          </span>
        </label>
      </div>

      {/* System Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
        {systems.map((item) => {
          const isSelected = value === item.id;
          return (
            <button
              key={item.id}
              type="button"
              disabled={disabled}
              onClick={() => onChange(item.id)}
              className={cn(
                'relative flex flex-col text-left p-4 sm:p-5 rounded-xl border transition-all cursor-pointer',
                isSelected
                  ? 'border-[#FC480D] bg-[#FFF8F5]'
                  : 'border-[#E0E6ED] bg-white hover:border-gray-300',
                disabled && 'cursor-not-allowed opacity-60'
              )}
            >
              <div className="flex items-center justify-between mb-3.5">
                <div
                  className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                    isSelected
                      ? 'bg-[#FFEFE8] text-[#FC480D]'
                      : 'bg-[#F5F6F7] text-[#0E1726]'
                  )}
                >
                  <Icon icon={item.icon} className="w-4 h-4" />
                </div>

                {/* Radio Dot Indicator */}
                <div
                  className={cn(
                    'w-4 h-4 rounded-full border flex items-center justify-center transition-colors',
                    isSelected
                      ? 'border-[#FC480D] bg-white'
                      : 'border-[#D5D5D5] bg-white'
                  )}
                >
                  {isSelected && (
                    <span className="w-2 h-2 rounded-full bg-[#FC480D]" />
                  )}
                </div>
              </div>

              <h4 className="text-sm font-semibold text-[#0E1726] mb-1">
                {item.title}
              </h4>
              <p className="text-xs text-[#8C8C8C] leading-relaxed">
                {item.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LoyaltySystemSelector;
